import React, { useState, forwardRef } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  Box,
  Button,
  Grid,
  Snackbar,
  Tooltip,
  Typography,
  CircularProgress,
  Tab,
  Tabs,
} from "@mui/material";
import MuiAlert from "@mui/material/Alert";
import { DataGrid, GridToolbar } from "@mui/x-data-grid";
import CheckIcon from "@mui/icons-material/Check";
import TransferService from "../services/TransferService";
import { useChosenEmployer } from "../contexts/EmployerContext";
import Labels from "../labels/Labels";
import RenderOnRole from "../components/RenderOnRole";
import CloseIcon from "@mui/icons-material/Close";
import SvbTransfers from "../components/SvbTransfers";

const Alert = forwardRef(function Alert(props, ref) {
  return <MuiAlert elevation={10} ref={ref} variant="filled" {...props} />;
});

export default function QueryTransfers() {
  const { chosenEmployerId } = useChosenEmployer();
  const queryClient = useQueryClient();
  const [tab, setTab] = useState("transfers");

  const [feedback, setFeedback] = useState({
    open: false,
    type: "error",
    message: "",
  });

  // Fetch transfers
  const {
    data,
    isLoading,
    isError,
    error,
    refetch,
  } = useQuery(
    ["transfers", chosenEmployerId],
    () => TransferService.fetchTransfers(chosenEmployerId),
    {
      enabled: !!chosenEmployerId,
      keepPreviousData: false,
    }
  );

  // Approve mutation
  const confirmMutation = useMutation(
    (transferId) => TransferService.confirmTransfer(transferId),
    {
      onSuccess: () => {
        setFeedback({
          open: true,
          type: "success",
          message: Labels.getLabel(
            "queryTransfers.approveSuccess",
            "Transfer approved successfully"
          ),
        });
        queryClient.invalidateQueries(["transfers"]);
        refetch();
      },
      onError: (err) => {
        const msg = err?.response?.data?.description
          ? err.response.data.description
          : err.message;
        setFeedback({
          open: true,
          type: "error",
          message: msg,
        });
      },
    }
  );

  // Reject mutation
  const rejectMutation = useMutation(
    (transferId) => TransferService.rejectTransfer(transferId),
    {
      onSuccess: () => {
        setFeedback({
          open: true,
          type: "success",
          message: Labels.getLabel(
            "queryTransfers.rejectSuccess",
            "Transfer rejected successfully"
          ),
        });
        queryClient.invalidateQueries(["transfers"]);
        refetch();
      },
      onError: (err) => {
        const msg = err?.response?.data?.description
          ? err.response.data.description
          : err.message;
        setFeedback({
          open: true,
          type: "error",
          message: msg,
        });
      },
    }
  );

  const handleFeedbackClose = (event, reason) => {
    if (reason === "clickaway") {
      return;
    }
    setFeedback((prev) => ({ ...prev, open: false }));
  };

  const onConfirmClick = (e, params) => {
    confirmMutation.mutate(params.idTransfer);
  };

  const onRejectClick = (e, params) => {
    rejectMutation.mutate(params.idTransfer);
  };

  const columns = [
    {
      field: "actions",
      headerName: Labels.getLabel("queryTransfers.column.actions", "Actions"),
      width: 150,
      renderCell: (params) => {
        if (!params.row.allowApprove) {
          return null;
        }
        return (
          <RenderOnRole roles={["transfers-confirm"]}>
            <Box sx={{ display: "flex", gap: 1 }}>
              <Tooltip
                title={Labels.getLabel(
                  "queryTransfers.tooltip.approve",
                  "Approve Transfer"
                )}
              >
                <Button
                  style={{ minWidth: "10px" }}
                  color="success"
                  onClick={(e) => onConfirmClick(e, params.row)}
                  disabled={confirmMutation.isLoading || rejectMutation.isLoading}
                >
                  <CheckIcon />
                </Button>
              </Tooltip>
              <Tooltip
                title={Labels.getLabel(
                  "queryTransfers.tooltip.reject",
                  "Reject Transfer"
                )}
              >
                <Button
                  style={{ minWidth: "10px" }}
                  color="error"
                  onClick={(e) => onRejectClick(e, params.row)}
                  disabled={confirmMutation.isLoading || rejectMutation.isLoading}
                >
                  <CloseIcon  />
                </Button>
              </Tooltip>
            </Box>
          </RenderOnRole>
        );
      },
    },
    {
      field: "idTransfer",
      headerName: Labels.getLabel("queryTransfers.column.id", "ID"),
      width: 90,
    },
    {
      field: "sourceCompanyName",
      headerName: Labels.getLabel("queryTransfers.column.from", "From"),
      width: 200,
    },
    {
      field: "destinationCompanyName",
      headerName: Labels.getLabel("queryTransfers.column.to", "To"),
      width: 200,
      renderCell: (params) => {
        const parentName = params.row.destinationParentName;
        if (parentName && parentName !== params.value) {
          return (
              <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    gap: 0.25,
                    lineHeight: 1.1,
                  }}
              >
                <Typography variant="caption" sx={{ lineHeight: 1.1 }}>
                  {params.row.destinationCompanyId} {params.value}
                </Typography>
                <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ lineHeight: 1.1, fontSize: "0.75rem" }}
                >
                  {parentName}
                </Typography>
              </Box>
          );
        }
        return (
            <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  gap: 0.25,
                  lineHeight: 1.1,
                }}
            >
              <Typography variant="caption" sx={{ lineHeight: 1.1 }}>
                {params.row.destinationCompanyId} {params.value}
              </Typography>
            </Box>
        );
      },
    },
    {
      field: "amount",
      headerName: Labels.getLabel("queryTransfers.column.amount", "Amount"),
      width: 120,
      renderCell: (params) => {
        return (
          <Typography sx={{ fontSize: "0.9rem", mb: 0 }} gutterBottom>
            {new Intl.NumberFormat("en-US", {
              style: "currency",
              currency: "USD",
            }).format(params.row.amount || 0)}
          </Typography>
        );
      },
    },
    {
      field: "externalTransferId",
      headerName: Labels.getLabel(
        "queryTransfers.column.externalTransferId",
        "External Transfer ID"
      ),
      width: 200,
    },
    {
      field: "externalSystem",
      headerName: Labels.getLabel(
        "queryTransfers.column.externalSystem",
        "External System"
      ),
      width: 120,
    },
    {
      field: "status",
      headerName: Labels.getLabel("queryTransfers.column.status", "Status"),
      width: 150,
      renderCell: (params) => {
        return (
          <Typography
            sx={{ fontSize: "0.9rem", mb: 0, fontWeight: "bold" }}
            gutterBottom
          >
            {params.row.status}
          </Typography>
        );
      },
    },
    {
      field: "createdDate",
      headerName: Labels.getLabel(
        "queryTransfers.column.createdDate",
        "Created Date"
      ),
      width: 180,
    },
    {
      field: "createdBy",
      headerName: Labels.getLabel(
        "queryTransfers.column.createdBy",
        "Created By"
      ),
      width: 200,
    },
  ];

  const rows = data?.data || [];

  return (
    <Box sx={{ width: "100%", height: "100%" }}>
      <Typography variant="h5">
        {Labels.getLabel("queryTransfers.title", "Transfers")}
      </Typography>
      <Tabs
        value={tab}
        onChange={(_event, value) => setTab(value)}
        aria-label={Labels.getLabel("queryTransfers.tabs", "Transfer views")}
        sx={{ mb: 2 }}
      >
        <Tab id="transfers-tab" aria-controls="transfers-panel" value="transfers" label={Labels.getLabel("queryTransfers.tab.transfers", "Transfers")} />
        <Tab id="svb-transfers-tab" aria-controls="svb-transfers-panel" value="svb" label={Labels.getLabel("queryTransfers.tab.svb", "SVB Transfers")} />
      </Tabs>
      {tab === "svb" ? (
        <Box role="tabpanel" id="svb-transfers-panel" aria-labelledby="svb-transfers-tab">
          <SvbTransfers key={chosenEmployerId} employerId={chosenEmployerId} />
        </Box>
      ) : (
        <Box role="tabpanel" id="transfers-panel" aria-labelledby="transfers-tab">
          <Grid container p={0} m={0} spacing={0}>
            <Grid item xs={12}>
              {isError && (
                <Typography color="error" gutterBottom>
                  {Labels.getLabel(
                    "queryTransfers.errorLoading",
                    "Error loading transfers"
                  )}
                  : {error?.message}
                </Typography>
              )}
            </Grid>
            <Grid item xs={12} sx={{ width: "100%", height: "80vh" }}>
              <DataGrid
                rows={rows}
                getRowId={(row) => row.idTransfer}
                columns={columns}
                pageSize={10}
                rowsPerPageOptions={[10, 25, 50, 100]}
                loading={isLoading}
                components={{
                  Toolbar: GridToolbar,
                  LoadingOverlay: CircularProgress,
                }}
                disableSelectionOnClick
                sx={{
                  backgroundColor: "white",
                  "& .MuiDataGrid-cell": {
                    borderBottom: "1px solid #f0f0f0",
                  },
                }}
              />
            </Grid>
          </Grid>
        </Box>
      )}
      <Snackbar
        open={feedback.open}
        autoHideDuration={6000}
        onClose={handleFeedbackClose}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert
          onClose={handleFeedbackClose}
          severity={feedback.type}
          sx={{ width: "100%" }}
        >
          {feedback.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
